import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import logger from '../utils/logger';
import { subscriptionService } from '../services/subscriptionService';
import { primaryFamilyCodeFromUserDoc, userDocHasFamilyContext } from '../utils/familyScope';

const AuthContext = createContext();

/** Merge Firebase Auth user with Firestore `users/{uid}` so `familyCode` is always set when any family field exists. */
function mergeAuthAndFirestoreUser(authUser, firestoreData) {
  if (!firestoreData) return { ...authUser };
  const primaryFc = primaryFamilyCodeFromUserDoc(firestoreData);
  return {
    ...authUser,
    ...firestoreData,
    ...(primaryFc ? { familyCode: primaryFc } : {}),
  };
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [subscription, setSubscription] = useState(null);

  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  // Fetch user data from Firestore
  const fetchUserData = async (user) => {
    if (!user) return null;
    
    try {
      await user.getIdToken();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      logger.error('Error fetching user data:', error);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Gate the entire app while we fetch Firestore data.
      // This prevents route guards from evaluating with stale currentUser
      // during sign-in / sign-out transitions.
      setLoading(true);

      if (user) {
        const data = await fetchUserData(user);

        // Auto-initialize a 14-day trial for family owners who existed before
        // subscription enforcement was deployed. Members (role === 'member') are
        // covered by the family owner's seat-based plan and must not receive their
        // own trial — doing so incorrectly marks them as TRIALING in the UI.
        const isOwnerOrLegacy = data?.role !== 'member';
        if (data && userDocHasFamilyContext(data) && !data.subscription && isOwnerOrLegacy) {
          try {
            await subscriptionService.initializeFreeSubscription(user.uid);
            // Re-fetch so subscription is included in the combined user object
            const refreshed = await fetchUserData(user);
            setUserData(refreshed);
            setSubscription(refreshed?.subscription || null);
            setCurrentUser(mergeAuthAndFirestoreUser(user, refreshed));
            setLoading(false);
            return;
          } catch (err) {
            logger.error('Auto-init subscription failed:', err);
          }
        }

        setUserData(data);

        // Extract subscription from Firestore data (may be null for new users)
        const sub = data?.subscription || null;
        setSubscription(sub);

        setCurrentUser(mergeAuthAndFirestoreUser(user, data));
      } else {
        setCurrentUser(null);
        setUserData(null);
        setSubscription(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Allow components (e.g. AccountCreationWizard) to force a re-read of the
  // Firestore user document after it has been updated.  Without this the
  // combined currentUser object stays stale until the next full page load.
  //
  // Options:
  //   waitForField (string) – if provided, retry the read with exponential
  //     back-off until the field is present on the document (up to ~8 s).
  //     This guards against Firestore eventual-consistency lag after writes.
  const refreshUserData = useCallback(async ({ waitForField } = {}) => {
    const user = auth.currentUser;
    if (!user) return;

    const maxRetries = waitForField ? 5 : 0;
    let delay = 500; // ms

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const data = await fetchUserData(user);

      if (!waitForField || (data && data[waitForField])) {
        // Mirror the onAuthStateChanged auto-init: give owners a 14-day trial
        // if they have a familyCode but no subscription yet. Skip for members —
        // they are covered by the family owner's seat and must not be given their
        // own trial (which would incorrectly show them as TRIALING).
        if (data && userDocHasFamilyContext(data) && !data.subscription && data?.role !== 'member') {
          try {
            await subscriptionService.initializeFreeSubscription(user.uid);
            const refreshed = await fetchUserData(user);
            setUserData(refreshed);
            setSubscription(refreshed?.subscription || null);
            setCurrentUser(mergeAuthAndFirestoreUser(user, refreshed));
            return;
          } catch (err) {
            logger.error('refreshUserData: auto-init subscription failed:', err);
          }
        }
        setUserData(data);
        setSubscription(data?.subscription || null);
        setCurrentUser(mergeAuthAndFirestoreUser(user, data));
        return;
      }

      // Field not present yet – wait before retrying
      if (attempt < maxRetries) {
        const waitTime = delay;
        await new Promise(resolve => setTimeout(resolve, waitTime));
        delay *= 2; // exponential back-off: 500, 1000, 2000, 4000, 8000
      }
    }

    // All retries exhausted – update with whatever we have so the app isn't
    // stuck, but log a warning.
    const data = await fetchUserData(user);
    logger.error(`refreshUserData: field "${waitForField}" still missing after retries`);
    setUserData(data);
    setSubscription(data?.subscription || null);
    setCurrentUser(mergeAuthAndFirestoreUser(user, data));
  }, []);

  const isMemberRole = userData?.role === 'member';

  const value = {
    currentUser,
    userData,
    signup,
    login,
    logout,
    loading,
    refreshUserData,
    subscription,
    // Members are covered by the family owner's seat — their own subscription
    // object (which may show an expired trial) must not block their access.
    isTrialActive: isMemberRole ? false : subscriptionService.isTrialActive(subscription),
    isAccessGranted: isMemberRole ? true : subscriptionService.isAccessGranted(subscription),
    trialDaysRemaining: isMemberRole ? 0 : subscriptionService.trialDaysRemaining(subscription),
    isInGracePeriod: isMemberRole ? false : subscriptionService.isInGracePeriod(subscription),
    graceDaysRemaining: isMemberRole ? 0 : subscriptionService.graceDaysRemaining(subscription),
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 